import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Settings, LayoutGrid, Send, Rocket, Radio, Video, Image as ImageIcon, TrendingUp, Users2, Trash2, ArrowLeft, Sparkles, DollarSign, Calendar, Target, Play, Pause, Save,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import MentionPicker from "@/components/MentionPicker";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import ImageUploader from "@/components/ImageUploader";
import LiveBroadcastDialog from "@/components/LiveBroadcastDialog";
import { PAGE_TYPE_META } from "@/pages/Pages";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_POST = { kind: "text", content: "", image_url: "", video_url: "" };
const EMPTY_CAMPAIGN = {
  title: "", description: "", image_url: "", target_url: "", cta: "Saiba mais",
  target_cities: "", target_interests: "", target_genders: [],
  budget_brl: 25, duration_days: 7,
};
const GENDERS = [
  { key: "male", label: "Masculino" },
  { key: "female", label: "Feminino" },
  { key: "other", label: "Outro" },
];

export default function PageAdmin() {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [posts, setPosts] = useState([]);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [pRes, postsRes, adsRes] = await Promise.all([
        axios.get(`${API}/pages/${pageId}`),
        axios.get(`${API}/pages/${pageId}/posts`),
        axios.get(`${API}/pages/${pageId}/ads`).catch(() => ({ data: [] })),
      ]);
      setPage(pRes.data);
      setPosts(postsRes.data || []);
      setAds(adsRes.data || []);
      if (!pRes.data?.is_owner) {
        toast.error("Você não tem permissão para gerenciar esta página.");
        navigate(`/pages/${pageId}`);
      }
    } catch { toast.error("Erro ao carregar página"); navigate("/pages"); }
    if (!silent) setLoading(false);
  };

  useEffect(() => { load(); }, [pageId]); // eslint-disable-line react-hooks/exhaustive-deps
  const reload = () => load({ silent: true });

  if (loading || !page) {
    return <div className="max-w-5xl mx-auto p-6"><Card className="p-10 text-center text-muted-foreground">Carregando painel…</Card></div>;
  }
  const meta = PAGE_TYPE_META[page.type] || { label: page.type, icon: LayoutGrid, color: "text-muted-foreground bg-muted" };
  const Icon = meta.icon;

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid={`page-admin-${page.page_id}`}>
      <div className="flex items-center gap-3">
        <Link to={`/pages/${page.page_id}`} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary">
          <ArrowLeft className="w-4 h-4" /> Voltar para a página
        </Link>
      </div>

      <Card className="p-5 flex flex-wrap items-center gap-4">
        <div className={`w-12 h-12 rounded-xl ${meta.color} flex items-center justify-center`}>
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Painel de gerenciamento · {meta.label}</div>
          <h1 className="text-xl md:text-2xl font-bold tracking-tight" data-testid="page-admin-name">{page.name}</h1>
        </div>
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><Users2 className="w-3.5 h-3.5" /> {page.followers_count} seguidores</span>
          <span className="flex items-center gap-1"><Send className="w-3.5 h-3.5" /> {posts.length} publicações</span>
          <span className="flex items-center gap-1"><Rocket className="w-3.5 h-3.5" /> {ads.length} anúncios</span>
        </div>
      </Card>

      <Tabs defaultValue="posts">
        <TabsList className="rounded-full flex-wrap h-auto">
          <TabsTrigger value="posts" data-testid="page-tab-posts" className="rounded-full gap-1"><Send className="w-4 h-4" /> Publicações</TabsTrigger>
          <TabsTrigger value="ads" data-testid="page-tab-ads" className="rounded-full gap-1"><Rocket className="w-4 h-4" /> Anúncios</TabsTrigger>
          <TabsTrigger value="lives" data-testid="page-tab-lives" className="rounded-full gap-1"><Radio className="w-4 h-4" /> Lives</TabsTrigger>
          <TabsTrigger value="settings" data-testid="page-tab-settings" className="rounded-full gap-1"><Settings className="w-4 h-4" /> Configurações</TabsTrigger>
        </TabsList>

        <TabsContent value="posts" className="mt-6 space-y-4"><PostsPanel pageId={pageId} posts={posts} reload={reload} /></TabsContent>
        <TabsContent value="ads" className="mt-6 space-y-4"><AdsPanel pageId={pageId} ads={ads} reload={reload} /></TabsContent>
        <TabsContent value="lives" className="mt-6"><LivesPanel /></TabsContent>
        <TabsContent value="settings" className="mt-6"><SettingsPanel page={page} reload={reload} /></TabsContent>
      </Tabs>
    </div>
  );
}

// ---------- Posts ----------
function PostsPanel({ pageId, posts, reload }) {
  const [form, setForm] = useState(EMPTY_POST);
  const [saving, setSaving] = useState(false);
  const [picked, setPicked] = useState([]);
  const contentRef = useRef(null);

  const publish = async () => {
    if (!form.content.trim() && !form.image_url && !form.video_url) return toast.error("Escreva algo, envie imagem ou vídeo");
    setSaving(true);
    try {
      const kind = form.video_url ? "video" : form.image_url ? (form.kind === "banner" ? "banner" : "image") : "text";
      const mention_ids = picked.filter((u) => form.content.toLowerCase().includes(`@${(u.name || "").split(" ")[0].toLowerCase()}`)).map((u) => u.user_id);
      await axios.post(`${API}/pages/${pageId}/posts`, { ...form, kind, mention_ids });
      toast.success("Publicado!");
      setForm(EMPTY_POST);
      setPicked([]);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm("Excluir?")) return;
    await axios.delete(`${API}/pages/${pageId}/posts/${id}`);
    reload();
  };

  return (
    <>
      <Card className="p-5 space-y-3" data-testid="page-post-composer">
        <div className="flex items-center gap-2 flex-wrap">
          {[
            { key: "text", label: "Texto", icon: Send },
            { key: "image", label: "Imagem", icon: ImageIcon },
            { key: "video", label: "Vídeo", icon: Video },
            { key: "banner", label: "Banner", icon: Sparkles },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setForm({ ...EMPTY_POST, kind: t.key })}
              data-testid={`page-post-kind-${t.key}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border flex items-center gap-1 transition-colors ${
                form.kind === t.key ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"
              }`}
            >
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>
        <div className="relative">
          <Textarea
            ref={contentRef}
            data-testid="page-post-content"
            rows={3}
            placeholder={form.kind === "banner" ? "Legenda do banner (opcional) — use @ para marcar amigos" : "O que você quer publicar? Use @ para marcar amigos"}
            value={form.content}
            onChange={(e) => setForm({ ...form, content: e.target.value })}
          />
          <MentionPicker value={form.content} onChange={(v) => setForm((f) => ({ ...f, content: v }))} inputRef={contentRef} onPick={(u) => setPicked((p) => (p.some((x) => x.user_id === u.user_id) ? p : [...p, u]))} />
        </div>
        {(form.kind === "image" || form.kind === "banner") && (
          <div className="flex gap-2">
            <Input
              data-testid="page-post-image"
              placeholder="URL da imagem"
              value={form.image_url}
              onChange={(e) => setForm({ ...form, image_url: e.target.value })}
            />
            <ImageUploader testid="page-post-image-upload" size="default" label="Imagem" onUploaded={(url) => setForm({ ...form, image_url: url })} />
          </div>
        )}
        {form.kind === "video" && (
          <div className="flex gap-2">
            <Input
              data-testid="page-post-video"
              placeholder="URL do vídeo"
              value={form.video_url}
              onChange={(e) => setForm({ ...form, video_url: e.target.value })}
            />
            <ImageUploader testid="page-post-video-upload" size="default" label="Vídeo" accept="video/*" onUploaded={(url) => setForm({ ...form, video_url: url })} />
          </div>
        )}
        {form.image_url && <img src={form.image_url} alt="" className="rounded-lg max-h-48 object-cover" />}
        <Button data-testid="page-post-submit" onClick={publish} disabled={saving} className="rounded-full gap-1">
          <Send className="w-4 h-4" /> Publicar
        </Button>
      </Card>

      <div className="space-y-3">
        {posts.length === 0 && (
          <Card className="p-8 text-center text-muted-foreground" data-testid="page-admin-posts-empty">Nenhuma publicação ainda.</Card>
        )}
        {posts.map((p) => (
          <Card key={p.post_id} className="p-4 flex gap-3" data-testid={`page-admin-post-${p.post_id}`}>
            {p.image_url && <img src={p.image_url} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />}
            {p.video_url && !p.image_url && (
              <div className="w-16 h-16 rounded-lg bg-black flex items-center justify-center flex-shrink-0">
                <Video className="w-6 h-6 text-white" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[10px] capitalize">{p.kind}</Badge>
                <span className="text-[11px] text-muted-foreground">{new Date(p.created_at).toLocaleString("pt-BR")}</span>
              </div>
              <div className="text-sm mt-1 line-clamp-2">{p.content || "—"}</div>
            </div>
            <button onClick={() => del(p.post_id)} className="text-destructive hover:opacity-80 self-start" data-testid={`page-admin-post-delete-${p.post_id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </Card>
        ))}
      </div>
    </>
  );
}

// ---------- Ads Manager (Facebook-style) ----------
function AdsPanel({ pageId, ads, reload }) {
  const [form, setForm] = useState(EMPTY_CAMPAIGN);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const estImpressions = useMemo(() => Math.max(100, Math.round(form.budget_brl * 60)), [form.budget_brl]);

  const toggleGender = (g) => setForm((f) => ({
    ...f,
    target_genders: f.target_genders.includes(g) ? f.target_genders.filter((x) => x !== g) : [...f.target_genders, g],
  }));

  const startEdit = (a) => {
    setEditingId(a.ad_id);
    setForm({
      title: a.title || "",
      description: a.description || "",
      image_url: a.image_url || "",
      target_url: a.target_url || "",
      cta: a.cta || "Saiba mais",
      target_cities: (a.target_cities || []).join(", "),
      target_interests: (a.target_interests || []).join(", "),
      target_genders: a.target_genders || [],
      budget_brl: a.campaign_budget_brl || 25,
      duration_days: a.campaign_duration_days || 7,
    });
  };

  const cancelEdit = () => { setEditingId(null); setForm(EMPTY_CAMPAIGN); };

  const save = async () => {
    if (!form.title.trim() || !form.description.trim()) return toast.error("Preencha título e descrição");
    setSaving(true);
    try {
      if (editingId) {
        await axios.patch(`${API}/pages/${pageId}/ads/${editingId}`, {
          title: form.title, description: form.description,
          image_url: form.image_url || null, target_url: form.target_url || null,
          cta: form.cta,
          target_cities: form.target_cities.split(",").map((s) => s.trim()).filter(Boolean),
          target_interests: form.target_interests.split(",").map((s) => s.trim()).filter(Boolean),
          target_genders: form.target_genders,
        });
        toast.success("Campanha atualizada");
      } else {
        const r = await axios.post(`${API}/pages/${pageId}/ads`, {
          title: form.title,
          description: form.description,
          image_url: form.image_url || null,
          target_url: form.target_url || null,
          cta: form.cta,
          target_cities: form.target_cities.split(",").map((s) => s.trim()).filter(Boolean),
          target_interests: form.target_interests.split(",").map((s) => s.trim()).filter(Boolean),
          target_genders: form.target_genders,
          budget_brl: Number(form.budget_brl) || 1,
          duration_days: parseInt(form.duration_days) || 1,
        });
        const approved = r.data?.moderation_status === "approved";
        toast.success(approved ? "Campanha criada e ativa!" : "Campanha criada! Aguardando moderação.");
      }
      cancelEdit();
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setSaving(false);
  };

  const toggleActive = async (a) => {
    try {
      await axios.patch(`${API}/pages/${pageId}/ads/${a.ad_id}`, { active: !a.active });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const del = async (id) => {
    if (!window.confirm("Excluir campanha?")) return;
    await axios.delete(`${API}/pages/${pageId}/ads/${id}`);
    if (editingId === id) cancelEdit();
    reload();
  };

  return (
    <>
      <Card className="p-5 space-y-4" data-testid="page-ad-manager">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
            <Rocket className="w-4 h-4 text-primary" />
          </div>
          <div>
            <div className="font-bold">Gerenciador de anúncios</div>
            <div className="text-xs text-muted-foreground">Impulsione conteúdo, defina público, orçamento e duração — igual ao Facebook.</div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <Input data-testid="camp-title" placeholder="Título do anúncio" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <Input data-testid="camp-cta" placeholder="Botão (ex: Comprar, Reservar)" value={form.cta} onChange={(e) => setForm({ ...form, cta: e.target.value })} />
          <Textarea data-testid="camp-description" className="md:col-span-2" rows={3} placeholder="Descrição / criativo" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="flex gap-2 md:col-span-2">
            <Input data-testid="camp-image" placeholder="URL da imagem/banner" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
            <ImageUploader testid="camp-image-upload" size="default" label="Foto" onUploaded={(url) => setForm({ ...form, image_url: url })} />
          </div>
          {form.image_url && <img src={form.image_url} alt="" className="rounded-lg max-h-32 object-cover md:col-span-2" />}
          <Input data-testid="camp-target-url" placeholder="Link de destino (https://...)" value={form.target_url} onChange={(e) => setForm({ ...form, target_url: e.target.value })} />
        </div>

        <div className="pt-3 border-t border-border space-y-3">
          <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Target className="w-3 h-3" /> Público-alvo</div>
          <Input data-testid="camp-target-cities" placeholder="Cidades (separadas por vírgula, ex: São Paulo, Rio)" value={form.target_cities} onChange={(e) => setForm({ ...form, target_cities: e.target.value })} />
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">Gênero:</span>
            {GENDERS.map((g) => (
              <button
                key={g.key}
                type="button"
                onClick={() => toggleGender(g.key)}
                data-testid={`camp-gender-${g.key}`}
                className={`px-3 py-1 rounded-full text-xs border transition-colors ${form.target_genders.includes(g.key) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}
              >
                {g.label}
              </button>
            ))}
          </div>
          <Input data-testid="camp-target-interests" placeholder="Interesses (ex: tecnologia, moda, esportes)" value={form.target_interests} onChange={(e) => setForm({ ...form, target_interests: e.target.value })} />
        </div>

        <div className="pt-3 border-t border-border grid md:grid-cols-2 gap-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground flex items-center gap-1 mb-2"><DollarSign className="w-3 h-3" /> Orçamento total</div>
            <div className="flex items-center gap-3">
              <Input
                data-testid="camp-budget"
                type="number"
                step="1"
                min="5"
                value={form.budget_brl}
                onChange={(e) => setForm({ ...form, budget_brl: Math.max(1, Number(e.target.value) || 0) })}
                className="max-w-[140px]"
              />
              <span className="text-sm">R$</span>
            </div>
            <Slider
              value={[Number(form.budget_brl)]}
              min={5}
              max={500}
              step={5}
              onValueChange={(v) => setForm({ ...form, budget_brl: v[0] })}
              className="mt-3"
              data-testid="camp-budget-slider"
            />
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground flex items-center gap-1 mb-2"><Calendar className="w-3 h-3" /> Duração da campanha</div>
            <div className="flex items-center gap-3">
              <Input
                data-testid="camp-duration"
                type="number"
                min="1"
                max="90"
                value={form.duration_days}
                onChange={(e) => setForm({ ...form, duration_days: Math.max(1, Math.min(90, parseInt(e.target.value) || 1)) })}
                className="max-w-[100px]"
              />
              <span className="text-sm">dias</span>
            </div>
            <Slider
              value={[Number(form.duration_days)]}
              min={1}
              max={30}
              step={1}
              onValueChange={(v) => setForm({ ...form, duration_days: v[0] })}
              className="mt-3"
              data-testid="camp-duration-slider"
            />
          </div>
        </div>

        <Card className="p-4 bg-primary/5 border-primary/30" data-testid="camp-estimate">
          <div className="text-xs font-semibold uppercase tracking-widest text-primary mb-1">Estimativa</div>
          <div className="text-sm">
            Aproximadamente <span className="font-bold">{estImpressions.toLocaleString("pt-BR")} impressões</span> em <span className="font-bold">{form.duration_days} dias</span> por <span className="font-bold">R$ {Number(form.budget_brl).toFixed(2)}</span>.
          </div>
        </Card>

        <div className="flex gap-2">
          <Button data-testid="camp-save" onClick={save} disabled={saving} className="rounded-full gap-1">
            <Save className="w-4 h-4" /> {editingId ? "Salvar alterações" : "Criar campanha"}
          </Button>
          {editingId && (
            <Button data-testid="camp-cancel" variant="outline" className="rounded-full" onClick={cancelEdit}>Cancelar</Button>
          )}
        </div>
      </Card>

      <div className="space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">Campanhas ({ads.length})</h3>
        {ads.length === 0 && (
          <Card className="p-8 text-center text-muted-foreground" data-testid="page-ads-empty">Nenhuma campanha criada.</Card>
        )}
        {ads.map((a) => {
          const modStatus = a.moderation_status || "approved";
          const running = a.active && modStatus === "approved" && (a.boost_impressions_left || 0) > 0;
          const ctr = a.impressions ? ((a.clicks || 0) / a.impressions * 100).toFixed(1) : "0.0";
          return (
            <Card key={a.ad_id} className={`p-4 flex gap-3 ${!a.active ? "opacity-60" : ""}`} data-testid={`page-ad-${a.ad_id}`}>
              {a.image_url && <img src={a.image_url} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-sm truncate">{a.title}</span>
                  {modStatus === "pending" && <Badge className="bg-amber-500 text-white text-[10px]">Em moderação</Badge>}
                  {modStatus === "rejected" && <Badge className="bg-red-500 text-white text-[10px]">Rejeitado</Badge>}
                  {running && <Badge className="bg-emerald-500 text-white text-[10px]"><Sparkles className="w-3 h-3 mr-1" /> Ativa</Badge>}
                  {!a.active && <Badge variant="outline" className="text-[10px]">Pausada</Badge>}
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{a.description}</p>
                <div className="text-[11px] text-muted-foreground mt-1 flex flex-wrap gap-3">
                  <span><DollarSign className="w-3 h-3 inline" /> R$ {Number(a.campaign_budget_brl || 0).toFixed(2)}</span>
                  <span><Calendar className="w-3 h-3 inline" /> {a.campaign_duration_days || 0}d</span>
                  <span><TrendingUp className="w-3 h-3 inline" /> {a.impressions || 0} impr · {a.clicks || 0} cliques · CTR {ctr}%</span>
                  <span>{a.boost_impressions_left || 0} restantes</span>
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <Button size="sm" variant="outline" className="rounded-full h-8 text-xs gap-1" onClick={() => startEdit(a)} data-testid={`page-ad-edit-${a.ad_id}`}>
                  Editar
                </Button>
                <Button size="sm" variant="outline" className="rounded-full h-8 text-xs gap-1" onClick={() => toggleActive(a)} data-testid={`page-ad-toggle-${a.ad_id}`}>
                  {a.active ? <><Pause className="w-3.5 h-3.5" /> Pausar</> : <><Play className="w-3.5 h-3.5" /> Ativar</>}
                </Button>
                <Button size="sm" variant="ghost" className="rounded-full h-8" onClick={() => del(a.ad_id)} data-testid={`page-ad-delete-${a.ad_id}`}>
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            </Card>
          );
        })}
      </div>
    </>
  );
}

// ---------- Lives ----------
function LivesPanel() {
  const [open, setOpen] = useState(false);
  return (
    <Card className="p-8 text-center space-y-4" data-testid="page-lives-panel">
      <div className="w-12 h-12 rounded-2xl bg-red-500/10 text-red-500 flex items-center justify-center mx-auto">
        <Radio className="w-6 h-6" />
      </div>
      <div>
        <h3 className="font-bold">Transmissões ao vivo</h3>
        <p className="text-sm text-muted-foreground">Inicie uma transmissão em nome da sua página. Ela aparecerá no feed principal.</p>
      </div>
      <Button onClick={() => setOpen(true)} className="rounded-full gap-1" data-testid="page-start-live">
        <Radio className="w-4 h-4" /> Iniciar transmissão
      </Button>
      <LiveBroadcastDialog open={open} onOpenChange={setOpen} />
    </Card>
  );
}

// ---------- Settings ----------
function SettingsPanel({ page, reload }) {
  const [form, setForm] = useState({
    name: page.name || "",
    description: page.description || "",
    category: page.category || "",
    avatar_url: page.avatar_url || "",
    cover_url: page.cover_url || "",
  });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/pages/${page.page_id}`, form);
      toast.success("Salvo!");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setSaving(false);
  };

  const del = async () => {
    if (!window.confirm("Excluir a página? Essa ação é irreversível.")) return;
    try {
      await axios.delete(`${API}/pages/${page.page_id}`);
      toast.success("Página excluída");
      navigate("/pages");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="space-y-4">
      <Card className="p-5 space-y-3" data-testid="page-settings">
        <Input data-testid="settings-name" placeholder="Nome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <Input data-testid="settings-category" placeholder="Categoria" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
        <Textarea data-testid="settings-description" rows={3} placeholder="Descrição" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <div className="flex gap-2">
          <Input data-testid="settings-avatar" placeholder="URL do avatar" value={form.avatar_url} onChange={(e) => setForm({ ...form, avatar_url: e.target.value })} />
          <ImageUploader testid="settings-avatar-upload" size="default" label="Avatar" onUploaded={(url) => setForm({ ...form, avatar_url: url })} />
        </div>
        <div className="flex gap-2">
          <Input data-testid="settings-cover" placeholder="URL da capa" value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} />
          <ImageUploader testid="settings-cover-upload" size="default" label="Capa" onUploaded={(url) => setForm({ ...form, cover_url: url })} />
        </div>
        <Button data-testid="settings-save" onClick={save} disabled={saving} className="rounded-full gap-1">
          <Save className="w-4 h-4" /> Salvar
        </Button>
      </Card>
      <Card className="p-5 border-destructive/40 bg-destructive/5">
        <div className="text-xs uppercase tracking-widest text-destructive font-bold mb-1">Zona de perigo</div>
        <p className="text-sm text-muted-foreground mb-3">Excluir a página remove todas as publicações e campanhas associadas.</p>
        <Button variant="destructive" onClick={del} data-testid="settings-delete-page" className="rounded-full gap-1">
          <Trash2 className="w-4 h-4" /> Excluir página
        </Button>
      </Card>
    </div>
  );
}
